import { memo, useMemo } from 'react';
import { FilterChips, FilterGroup } from '../../components/FilterChips';
import { useReferralRoleTranslations } from '../../hooks/translations/useReferralRoleTranslations';
import { usePlanOptions } from '../../hooks/usePlanOptions';
import { useT } from '../../translation/src';
import { PartnerRelationFilter, ReferralRole, SubscriptionStatusFilter, SubscriptionType } from '../../types/graphqlTypes';
import { useSubscriptionStatusLabels } from '../../components/SubscriptionStatusTags';

/** The admin center's organisation filters, but the name search (see InputSuperadminOrganisationFilters). */
// A type (not an interface) so it fits FilterValues' index signature.
export type OrganisationFilterValues = {
  subscriptionType?: SubscriptionType[];
  partnerRole?: ReferralRole[];
  subscriptionStatus?: SubscriptionStatusFilter[];
  acquiredBy?: PartnerRelationFilter[];
  managedBy?: PartnerRelationFilter[];
  billedTo?: PartnerRelationFilter[];
};
type FilterKey = keyof OrganisationFilterValues;

// Managed by Pythagoras by default: the customers a partner manages are the partner's to look after.
export const DEFAULT_ORGANISATION_FILTERS: OrganisationFilterValues = { managedBy: [PartnerRelationFilter.Pythagoras] };

const useFilterGroups = (): FilterGroup<OrganisationFilterValues>[] => {
  const plans = usePlanOptions();
  const roles = useReferralRoleTranslations();
  const statuses = useSubscriptionStatusLabels();
  const status = useT('status', { swc: true });
  const subscription = useT('subscription', { swc: true });
  const role = useT('partner role', { swc: true });
  const acquiredBy = useT('acquired by', { swc: true });
  const managedBy = useT('managed by', { swc: true });
  const billedTo = useT('billed to', { swc: true });
  const pythagoras = useT('Pythagoras');
  const direct = useT('direct', { swc: true });
  const itself = useT('itself', { swc: true });
  const aPartner = useT('a partner', { swc: true });
  return useMemo(() => {
    const relation = (key: FilterKey, title: string, none: string): FilterGroup<OrganisationFilterValues> => ({
      key,
      title,
      options: [
        { value: PartnerRelationFilter.Pythagoras, label: none },
        { value: PartnerRelationFilter.Partner, label: aPartner },
      ],
    });
    return [
      { key: 'subscriptionType', title: subscription, options: plans },
      { key: 'subscriptionStatus', title: status, options: Object.values(SubscriptionStatusFilter).map((value) => ({ value, label: statuses[value] })) },
      { key: 'partnerRole', title: role, options: Object.values(ReferralRole).map((value) => ({ value, label: roles[value] })) },
      relation('acquiredBy', acquiredBy, direct),
      relation('managedBy', managedBy, pythagoras),
      relation('billedTo', billedTo, itself),
    ];
  }, [aPartner, acquiredBy, billedTo, direct, itself, managedBy, plans, pythagoras, role, roles, status, statuses, subscription]);
};

interface OrganisationFilterBarProps {
  value: OrganisationFilterValues;
  onChange: (value: OrganisationFilterValues) => void;
}
// The admin center's filters as the shared filter button with chips.
const OrganisationFilterBar_: React.FC<OrganisationFilterBarProps> = ({ value, onChange }) => {
  const groups = useFilterGroups();
  return <FilterChips groups={groups} value={value} onChange={onChange} defaults={DEFAULT_ORGANISATION_FILTERS} />;
};
export const OrganisationFilterBar = memo(OrganisationFilterBar_);
